import {
  PropsWithChildren,
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  Dispatch,
  SetStateAction,
  useEffect,
} from "react";

interface IStepContext {
  step: number;
  goToNextStep: () => void;
  goToPrevStep: () => void;
  reset: () => void;
  canGoToNextStep: boolean;
  canGoToPrevStep: boolean;
  setStep: Dispatch<SetStateAction<number>>;
}

export const StepContext = createContext<IStepContext>(null!);

export interface StepProviderProps extends PropsWithChildren {
  initialStep?: number;
  maxStep: number;
}

type setStepCallbackType = (step: number | ((step: number) => number)) => void;

export const StepProvider = ({
  initialStep = 0,
  maxStep,
  children,
}: StepProviderProps) => {
  const [currentStep, setCurrentStep] = useState(initialStep);

  const canGoToNextStep = useMemo(
    () => currentStep + 1 <= maxStep,
    [currentStep, maxStep],
  );

  const canGoToPrevStep = useMemo(() => currentStep - 1 >= 0, [currentStep]);

  const setStep = useCallback<setStepCallbackType>(
    (step) => {
      // Allow value to be a function so we have the same API as useState
      const newStep = step instanceof Function ? step(currentStep) : step;

      if (newStep >= 1 && newStep <= maxStep) {
        setCurrentStep(newStep);
        return;
      }

      throw new Error("Step not valid");
    },
    [maxStep, currentStep],
  );

  const goToNextStep = useCallback(() => {
    if (canGoToNextStep) {
      setCurrentStep((currentStep) => currentStep + 1);
    }
  }, [canGoToNextStep]);

  const goToPrevStep = useCallback(() => {
    if (canGoToPrevStep) {
      setCurrentStep((currentStep) => currentStep - 1);
    }
  }, [canGoToPrevStep]);

  const reset = useCallback(() => {
    setCurrentStep(1);
  }, []);

  return (
    <StepContext.Provider
      value={{
        step: currentStep,
        goToNextStep,
        goToPrevStep,
        canGoToNextStep,
        canGoToPrevStep,
        setStep,
        reset,
      }}
    >
      {children}
    </StepContext.Provider>
  );
};

export const useStepContext = (currentStep?: number) => {
  const context = useContext(StepContext);

  useEffect(() => {
    if (currentStep !== undefined) {
      context.setStep(currentStep);
    }
  }, [currentStep, context.setStep]);

  return context;
};
